import type { AnswerMap, JsonValue } from './types';

type Row = Record<string, JsonValue>;
export function personOptions(answers: AnswerMap): Array<{ value: string; label: string }> {
	return ['client', 'spouse'].flatMap((person) => {
		const name = answers[`${person}.full_name`];
		return typeof name === 'string' && name.trim() ? [{ value: `person:${person}`, label: name }] : [];
	});
}

export function personName(value: JsonValue | undefined, answers: AnswerMap): string {
	if (typeof value !== 'string') return '';
	if (value === 'person:client' || value === 'person:spouse') {
		return personOptions(answers).find((option) => option.value === value)?.label ?? 'Name not entered';
	}
	return value;
}

export function moneyCents(value: JsonValue | undefined): number | null {
	if (typeof value !== 'string' && typeof value !== 'number') return null;
	const raw = String(value)
		.trim()
		.replace(/^\$\s*/u, '');
	if (!/^(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d{1,2})?$/u.test(raw)) return null;
	const result = Math.round(Number(raw.replaceAll(',', '')) * 100);
	return Number.isSafeInteger(result) && result <= Number.MAX_SAFE_INTEGER / 12 ? result : null;
}

export function incomeAmounts(row: Row): { monthly: number; annual: number } | null {
	// Old rows remain explicitly annual. A cleared new amount must NOT resurrect a legacy value.
	const modern = Object.hasOwn(row, 'amount') || Object.hasOwn(row, 'frequency');
	const cents = moneyCents(modern ? row.amount : row.annual_amount);
	const frequency = modern ? row.frequency : 'annual';
	if (cents === null || (frequency !== 'monthly' && frequency !== 'annual')) return null;
	const annual = frequency === 'monthly' ? cents * 12 : cents;
	return { monthly: Math.round(annual / 12), annual };
}

export function incomeSummary(answers: AnswerMap) {
	const value = answers['assets.social_security_pension'];
	const rows = Array.isArray(value) ? value.filter((row): row is Row => !!row && typeof row === 'object' && !Array.isArray(row)) : [];
	const totals = new Map<string, { owner: string; annual: number }>();
	let incomplete = 0;
	const entries = rows.map((row) => {
		const owner = personName(row.owner, answers);
		const amounts = incomeAmounts(row);
		const personReference = row.owner === 'person:client' || row.owner === 'person:spouse';
		const named = !personReference || personOptions(answers).some((option) => option.value === row.owner);
		if (amounts && owner && named) {
			const matchingPeople = personOptions(answers).filter((option) => option.label.trim().toUpperCase() === owner.trim().toUpperCase());
			const key = personReference
				? String(row.owner)
				: matchingPeople.length === 1
					? matchingPeople[0].value
					: `legacy:${owner.trim().toUpperCase()}`;
			const total = totals.get(key) ?? { owner, annual: 0 };
			total.annual += amounts.annual;
			totals.set(key, total);
		} else if (Object.entries(row).some(([key, value]) => key !== '_id' && value !== '')) incomplete++;
		return { row, owner, amounts };
	});
	return { entries, incomplete, totals: [...totals.values()].map((total) => ({ ...total, monthly: Math.round(total.annual / 12) })) };
}

export function displayCents(cents: number | null | undefined): string {
	return cents === null || cents === undefined
		? ''
		: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}
